import { FC, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { Typography } from '@dreamteam-monorepo/ui';
import { useLeaguesStore } from '@/stores/LeaguesStore';
import { useAuthStore } from '@/stores/AuthStore';
import { BELeague } from '@/lib/leagueMappers';
import { getErrorMessage } from '@/lib/network';
import { showGhLogin } from '@/lib/ghAuth';
import { ContentAdSlot } from '@/ads';
import { trackFantasyEvent } from '@/analytics/fantasyEventTracking';

const JoinLeagueByLink: FC = () => {
  const { code = '' } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { sessionState, userId } = useAuthStore();
  const { getLeagueByCode, joinLeagueByCode, joining, error } = useLeaguesStore();
  const hasSettledSession = sessionState !== 'resolving';

  const [league, setLeague] = useState<BELeague | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!hasSettledSession || !code) return;
    setLoading(true);
    setNotFound(false);
    getLeagueByCode(code)
      .then(setLeague)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [hasSettledSession, code, getLeagueByCode]);

  const handleJoin = async () => {
    if (!userId) {
      toast.error('You must be logged in to join a league');
      showGhLogin();
      return;
    }
    try {
      const leagueId = await joinLeagueByCode(code, userId);
      trackFantasyEvent('leagueJoinedByCode');
      navigate(`/leagues/${leagueId}`, { state: { justJoined: true } });
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not join the league. Please try again.'));
    }
  };

  if (loading) {
    return (
      <div className="components light flex flex-col items-center justify-center min-h-[50vh] p-[--padding-md]">
        <div className="h-8 w-48 bg-gray-200 animate-pulse rounded" />
      </div>
    );
  }

  if (error === 401) {
    return (
      <div className="components light flex flex-col items-center justify-center gap-4 min-h-[50vh] p-[--padding-md] max-w-[846px] mx-auto w-full">
        <Typography
          as="h1"
          variant="headline050"
          className="text-[--color-neutral-900] !font-[900] uppercase"
        >
          Need to sign in
        </Typography>
        <Typography as="p" variant="paragraph020" className="text-[--color-neutral-600]">
          Leagues are only available to signed-in users. Please sign in to join this league.
        </Typography>
        <a
          href="https://login.thesun.co.uk/oidc/rp/login/dreamteam?gotoUrl=https://pre-gaming.uat-dreamteamfc.com/"
          className="mt-4 px-6 py-3 bg-[--color-blue-200] text-white rounded-[--corner-xs] font-semibold"
        >
          Sign in
        </a>
      </div>
    );
  }

  if (notFound || !league) {
    return (
      <div className="components light flex flex-col items-center justify-center gap-4 min-h-[50vh] p-[--padding-md] max-w-[846px] mx-auto w-full">
        <Typography
          as="h1"
          variant="headline050"
          className="text-[--color-neutral-900] !font-[900]"
        >
          LEAGUE NOT FOUND
        </Typography>
        <Typography as="p" variant="paragraph020" className="text-[--color-neutral-600]">
          This invite link is invalid or the league no longer exists.
        </Typography>
        <button
          type="button"
          onClick={() => navigate('/leagues')}
          className="mt-4 px-6 py-3 bg-[--color-blue-200] text-white rounded-[--corner-xs] font-semibold"
        >
          Back to Leagues
        </button>
      </div>
    );
  }

  return (
    <div className="components light flex flex-col">
      <div className="flex flex-col gap-6 p-[--padding-md] max-w-[846px] mx-auto w-full pt-8">
        <Typography
          as="h1"
          variant="headline050"
          className="text-[--color-neutral-900] !font-[900]"
        >
          YOU&apos;VE BEEN INVITED
        </Typography>

        <div className="flex flex-col gap-3 bg-white rounded-[--corner-sm] p-6 shadow-sm border border-[#eae9fd]">
          <Typography
            as="p"
            variant="paragraph010"
            className="text-[--color-neutral-600] uppercase"
          >
            League
          </Typography>
          <Typography
            as="h2"
            variant="headline040"
            className="text-[--color-neutral-900] !font-[900]"
          >
            {league.name}
          </Typography>
          <Typography as="p" variant="paragraph020" className="text-[--color-neutral-600]">
            {league.is_public ? 'Public league' : 'Private league'} · Starting GW{' '}
            {league.start_gameweek || 1}
          </Typography>
        </div>

        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={handleJoin}
            disabled={joining}
            className="w-full py-4 bg-[--color-blue-200] text-white rounded-[--corner-xs] font-semibold text-base disabled:opacity-60"
          >
            {joining ? 'Joining...' : 'Join League'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/leagues')}
            className="w-full py-4 border border-[--color-blue-200] text-[--color-blue-200] rounded-[--corner-xs] font-semibold text-base"
          >
            Cancel
          </button>
        </div>
      </div>
      <ContentAdSlot id="ad-boa-leaderboard" />
    </div>
  );
};

export default JoinLeagueByLink;
